import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {isDefined} from '../../../commons/commons';
import {SmagApiService} from '../../../api/smag-api.service';
import {UserDisableReason} from 'smag-commons/dto/api';
import {I18NService} from '../../../i18n/i18n.service';
import {MemberAccount} from '../../ec-members/account';
import {ECRepresentative, ECRepresentativePersonalData, HierarchyEntity, UpdateECRepresentativeRequest} from 'smag-commons/cfg/hierarchy';
import {FormControl} from '@angular/forms';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {MatDialog} from '@angular/material/dialog';
import {addressToString, TenantsAndNetworks} from '../ec-representatives.component';
import {map, startWith} from 'rxjs/operators';
import {I18NDatePipe} from '../../../i18n/i18n.pipe';
import {AuthService} from 'auth-commons';
import {isPasswordExpired} from '../../../shared/ec-members';

const DIALOG_WIDTH = 1200;
const REMOVE_DIALOG_WIDTH = 920;

@Component({
    selector: 'app-representative-details',
    templateUrl: './representative-details.component.html',
    styleUrls: ['./representative-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RepresentativeDetailsComponent implements OnInit {

    @ViewChild('assignCommunities') private assignCommunitiesDialog: TemplateRef<any>;
    @ViewChild('removeCommunity') private removeCommunityDialog: TemplateRef<any>;

    @Input() member: ECRepresentative;
    @Input() account: MemberAccount;
    @Input() show2fa: boolean;
    @Input() canUpdate: boolean;
    @Input() tenantsAndNetworks: TenantsAndNetworks[];
    @Output() disableAccount: EventEmitter<void> = new EventEmitter<void>();
    @Output() reactivateAccount: EventEmitter<void> = new EventEmitter<void>();
    @Output() forcePwdChange: EventEmitter<void> = new EventEmitter<void>();
    @Output() reset2FA: EventEmitter<void> = new EventEmitter<void>();
    @Output() updateTenantAndNetworks: EventEmitter<HierarchyEntity[]> = new EventEmitter<HierarchyEntity[]>();

    readonly commPoolFilter: FormControl;
    assignations: HierarchyEntity[];
    communitiesPool: HierarchyEntity[];
    arrSelectableComms: HierarchyEntity[];
    selectedCommunity: FormControl;
    disableEditRep: boolean;
    tempC: HierarchyEntity;
    i18nDatePipe: I18NDatePipe;

    constructor(private readonly changeDetectorRef: ChangeDetectorRef,
                private readonly apiService: SmagApiService,
                private readonly i18n: I18NService,
                private readonly dialog: MatDialog,
                private readonly msgBoxService: MsgBoxService,
                private readonly authService: AuthService) {
        this.assignations = [];
        this.communitiesPool = [];
        this.arrSelectableComms = [];
        this.selectedCommunity = new FormControl();
        this.disableEditRep = false;
        this.commPoolFilter = new FormControl();
        this.tempC = null;
        this.i18nDatePipe = new I18NDatePipe(this.i18n, this.authService);
    }

    ngOnInit() {
        this.updateAssignationsAndPool();
        this.commPoolFilter.valueChanges.pipe(
            startWith(''),
            map(value => {
                if (!value || value.length === 0) {
                    this.tempC = null;
                }
                return this.filterCommsByName(value);
            })
        ).subscribe(d => {
            this.arrSelectableComms = d;
        });
    }

    get canRenewPassword(): boolean {
        return this.canUpdate && !!this.account && !this.account.mustChangePassword;
    }

    get canReset2FA(): boolean {
        return this.canUpdate && !!this.account && this.account.enabled2fa;
    }

    get isEditDisabled(): boolean {
        return this.disableEditRep;
    }

    updateAssignationsAndPool() {
        this.assignations = this.tenantsAndNetworks
            .filter(t => (t.tenantId === this.member.tenant) || (!t.tenantId && !this.member.tenant))[0].networks
            .filter(n => this.member.network_ids.includes(n.id));
        this.communitiesPool = this.tenantsAndNetworks
            .filter(t => (t.tenantId === this.member.tenant) || (!t.tenantId && !this.member.tenant))[0].freeNetworks
            .filter(c => !(this.assignations.map(a => a.id).includes(c.id)));
    }

    emitUpdateToParent(diff: HierarchyEntity[]) {
        this.updateTenantAndNetworks.emit(diff);
    }

    isDefined(value: any): boolean {
        return isDefined(value);
    }

    mkAddress(): string {
        return addressToString(this.member.personal_data?.address);
    }

    mkDisableReasonMessage(reason: UserDisableReason) {
        const disabledData = this.i18nDatePipe.transform(this.account.disabledOn);
        switch (reason) {
            case UserDisableReason.DISABLED_BY_ADMIN:
                return this.i18n.translate('Manually disabled since {0}', disabledData);
            case UserDisableReason.INACTIVE:
                return this.i18n.translate('Automatically disabled since {0} due to inactivity', disabledData);
            case UserDisableReason.FAILED_LOGIN:
                return this.i18n.translate('Automatically disabled since {0} due to too many failed logins', disabledData);
            default:
                return this.i18n.translate('Disabled member since {0}', disabledData);
        }
    }

    isPasswordOk(account: MemberAccount): boolean {
        return !account.mustChangePassword && !isPasswordExpired(account);
    }

    onClickDisableAccount() {
        this.disableAccount.emit();
    }

    onClickReactivateAccount() {
        this.reactivateAccount.emit();
    }

    onClickForcePwdChange() {
        this.forcePwdChange.emit();
    }

    onClickReset2FA() {
        this.reset2FA.emit();
    }

    getCommunityFrom(id: string) {
        return this.tenantsAndNetworks
            .filter(t => (t.tenantId === this.member.tenant) || (!t.tenantId && !this.member.tenant))[0]?.networks
            .filter(c => c.id === id)[0]?.name;
    }

    openAssignCommsDialog(member: ECRepresentative) {
        // Refresh in case another detail component changed the assignations
        this.updateAssignationsAndPool();
        this.commPoolFilter.setValue('');
        const tempAss = [];
        this.assignations.forEach(x => tempAss.push(x));
        // Open dialog
        this.dialog.open(this.assignCommunitiesDialog, {
            hasBackdrop: true,
            minWidth: `${DIALOG_WIDTH}px`
        }).afterClosed().subscribe(bool => {
            if (bool) {
                this.disableEditRep = true;
                // Create differential to speed up parent component
                const differential: HierarchyEntity[] = [];
                tempAss.forEach(entry => {
                    if (!this.assignations.includes(entry)) {
                        differential.push(entry);
                    }
                });
                this.assignations.forEach(entry => {
                    if (!tempAss.includes(entry)) {
                        differential.push(entry);
                    }
                });
                const updatedMember: UpdateECRepresentativeRequest = {
                    tenant: this.member.tenant,
                    representative_type: this.member.representative_type,
                    network_ids: this.assignations.map(ass => ass.id),
                    personal_data: this.member.personal_data
                };
                this.apiService.updateRepresentative(this.member.id, updatedMember)
                    .then(() => {
                        this.msgBoxService.info(this.i18n.translate('Community assignments updated'));
                        // Update member data and reload
                        member.network_ids = this.assignations.map(ass => ass.id);
                        this.emitUpdateToParent(differential);
                        setTimeout(() => {
                            this.disableEditRep = false;
                            this.changeDetectorRef.detectChanges();
                        }, 600);
                    })
                    .catch(err => {
                        this.msgBoxService.error(this.i18n.translate('An error occurred while updating the representative'), err);
                        this.disableEditRep = false;
                        this.changeDetectorRef.detectChanges();
                    });
            } else {
                // Reset assignations
                this.updateAssignationsAndPool();
            }
        });
    }

    openRemoveCommunityDialog(id: string) {
        const currentComm = this.tenantsAndNetworks.map(t => t.networks).filter(x => x.map(y => y.id).includes(id))[0]
            .filter(c => c.id === id)[0];
        this.dialog.open(this.removeCommunityDialog, {
            hasBackdrop: true,
            minWidth: `${REMOVE_DIALOG_WIDTH}px`,
            data: {
                community: currentComm
            }
        }).afterClosed().subscribe(remove => {
            if (remove) {
                this.apiService.deleteNetworkToRepresentative(this.member.id, id)
                    .then(() => {
                        this.msgBoxService.info(this.i18n.translate('Network successfully unassigned'));
                        this.member.network_ids = this.member.network_ids.filter(n => n !== id);
                        this.emitUpdateToParent([currentComm]);
                    })
                    .catch(err => {
                        this.msgBoxService.error(this.i18n.translate('Error during assignation removal'), err);
                    });
            }
        });
    }

    getNameFrom(data: ECRepresentativePersonalData) {
        return data.company_name || data.first_name + ' ' + data.last_name;
    }

    onSelectCommunity(comm: any) {
        this.tempC = this.arrSelectableComms.filter(a => a.name === comm.source.value)[0];
    }

    onAddCommunity() {
        this.assignations.push(this.tempC);
        this.communitiesPool = this.communitiesPool.filter(c => c.id !== this.tempC.id);
        this.commPoolFilter.setValue('');
        this.tempC = null;
    }

    removeAssignation(ass: HierarchyEntity) {
        this.communitiesPool.push(ass);
        this.assignations = this.assignations.filter(assignation => assignation.id !== ass.id);
        this.commPoolFilter.setValue('');
        this.tempC = null;
    }

    contactEmail(email: string) {
        window.open('mailto:' + email);
    }

    sortByName = (a, b) => {
        return a.value.name > b.value.name ? 1 : -1;
    }

    private filterCommsByName(text: string): HierarchyEntity[] {
        if (typeof text === 'string') {
            return this.communitiesPool.filter(c => {
                return c.name.toLowerCase().includes(text.toLowerCase());
            });
        }
        return this.communitiesPool;
    }
}
